import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPlus, faPen, faTrash, faSearch,
  faUser, faRobot, faPalette, faCheck, faChevronDown, faGripVertical
} from '@fortawesome/free-solid-svg-icons';
import { getAuthHeaders, getUser, setUser, API_BASE_URL } from './auth';
import './UserManagement.css';

const API_USERS = `${API_BASE_URL}/api/users`;
const API_FUNCTIONS = `${API_BASE_URL}/api/bot_functions`;

const LLM_MODELS = [
  { value: 'gpt-5.2', label: 'GPT-5.2 (OpenAI)' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (OpenAI)' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (OpenAI)' },
  { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet (Anthropic)' },
  { value: 'mistral-large-latest', label: 'Mistral Large (Mistral)' },
  { value: 'gemini-2.0-flash-exp', label: 'Gemini 2.0 Flash (Google)' },
  { value: 'mixtral-8x7b-instruct', label: 'Mixtral 8x7B (Euria)' },
];

const VIEW_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'widget_frank', label: 'Widget Frank' },
  { value: 'widget_frank2', label: 'Widget Frank 2' },
];

const LOGO_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'pictet', label: 'Pictet' },
];

const EXPANDABLE_OPTIONS = [
  { value: 'chat', label: 'Chat', desc: 'AI assistant with voice support' },
  { value: 'chat_noveras', label: 'Chat Noveras', desc: 'AI assistant with Noveras tools' },
  { value: 'chat_widget', label: 'Chat Widget', desc: 'Compact chat widget overlay' },
  { value: 'client_notes', label: 'Client Notes', desc: 'Customer visit notes management' },
  { value: 'performance', label: 'Performance', desc: 'Portfolio performance overview' },
  { value: 'region_allocation', label: 'Region Allocation', desc: 'Geographic distribution analysis' },
  { value: 'assetclass_allocation', label: 'Asset Class Allocation', desc: 'Asset class distribution overview' },
  { value: 'currency_allocation', label: 'Currency Allocation', desc: 'Currency exposure breakdown' },
  { value: 'asset_under_mgt_mgr', label: 'AuM (Manager)', desc: 'Assets under management for managers' },
  { value: 'kpi_mgr', label: 'KPIs (Manager)', desc: 'Key performance indicators YTD' },
  { value: 'net_new_asset_mgr', label: 'Net New Assets (Manager)', desc: 'Net new money inflow for managers' },
  { value: 'income_information_mgr', label: 'Income (Manager)', desc: 'Income analysis for managers' },
  { value: 'new_clients_mgr', label: 'New Clients (Manager)', desc: 'New client acquisition for managers' },
  { value: 'asset_under_mgt_adv', label: 'AuM (Advisor)', desc: 'Assets under management for advisors' },
  { value: 'net_new_asset_adv', label: 'Net New Assets (Advisor)', desc: 'Net new money inflow for advisors' },
  { value: 'new_clients_adv', label: 'New Clients (Advisor)', desc: 'New client acquisition for advisors' },
  { value: 'contacted_clients_adv', label: 'Contacted Clients', desc: 'Client contact tracking for advisors' },
];

const EMPTY_USER = {
  first_name: '', last_name: '', email: '', username: '', hashed_password: '',
  status: 'active', language: 'de',
  llm_config: { llm_model: 'gpt-5.2', bot_function_ids: [], system_prompt: '', temperature: 0.7 },
  ui_config: { view: '', logo: 'default', show_pdf_download: false, pdf_url: '', pdf_download_name: '', show_breaches_button: false, expandables: [] },
};

const deepClone = (o) => JSON.parse(JSON.stringify(o));
const initials = (f, l) => ((f?.[0] || '') + (l?.[0] || '')).toUpperCase() || '?';

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────
function UserManagement({ onBack, onUserUpdated }) {
  const [users, setUsers] = useState([]);
  const [botFunctions, setBotFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [collapsed, setCollapsed] = useState({ user: true, llm: true, ui: true });
  const [drag, setDrag] = useState(null);

  // ── Fetch data ──
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_USERS, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load users');
      setUsers(await res.json());
    } catch (e) {
      flash('error', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFunctions = useCallback(async () => {
    try {
      const res = await fetch(API_FUNCTIONS, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load functions');
      setBotFunctions(await res.json());
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchFunctions(); }, [fetchUsers, fetchFunctions]);

  // ── Toast ──
  const flash = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Navigation ──
  const toggleSection = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  const openEdit = (user) => { setSelected(user); setIsNew(false); setForm(deepClone(user)); setCollapsed({ user: true, llm: true, ui: true }); setConfirmId(null); };
  const openNew = () => { setSelected({}); setIsNew(true); setForm(deepClone(EMPTY_USER)); setCollapsed({ user: true, llm: true, ui: true }); setConfirmId(null); };
  const closeDetail = () => { setSelected(null); setForm(null); setIsNew(false); setConfirmId(null); };

  // ── Field setter (supports dot paths) ──
  const set = (path, val) => {
    setForm(prev => {
      const next = deepClone(prev);
      const keys = path.split('.');
      let o = next;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = val;
      return next;
    });
  };

  // ── Toggle helpers ──
  const toggleFuncId = (id) => {
    const ids = form.llm_config.bot_function_ids || [];
    set('llm_config.bot_function_ids', ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  const toggleExpandable = (val) => {
    const list = form.ui_config.expandables || [];
    set('ui_config.expandables', list.includes(val) ? list.filter(x => x !== val) : [...list, val]);
  };

  // ── Drag & drop reorder ──
  const handleDragStart = (e, type, fromIdx) => {
    e.dataTransfer.effectAllowed = 'move';
    setDrag({ type, fromIdx, overIdx: null });
  };
  const handleDragOver = (e, type, idx) => {
    e.preventDefault();
    if (drag?.type === type) setDrag(prev => prev ? { ...prev, overIdx: idx } : null);
  };
  const handleDrop = (e, type, toIdx) => {
    e.preventDefault();
    if (!drag || drag.type !== type || drag.fromIdx === toIdx) { setDrag(null); return; }
    if (type === 'func') {
      const ids = [...(form.llm_config.bot_function_ids || [])];
      const [moved] = ids.splice(drag.fromIdx, 1);
      ids.splice(toIdx, 0, moved);
      set('llm_config.bot_function_ids', ids);
    } else {
      const list = [...(form.ui_config.expandables || [])];
      const [moved] = list.splice(drag.fromIdx, 1);
      list.splice(toIdx, 0, moved);
      set('ui_config.expandables', list);
    }
    setDrag(null);
  };

  // ── Save ──
  const handleSave = async () => {
    try {
      if (isNew) {
        const res = await fetch(API_USERS, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(form),
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Create failed'); }
        flash('success', 'User created successfully.');
      } else {
        const body = { ...form };
        delete body.id; delete body.created_at; delete body.last_login;
        const res = await fetch(`${API_USERS}/${selected.id}`, {
          method: 'PUT',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body),
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Update failed'); }
        const currentUser = getUser();
        if (currentUser && currentUser.username === selected.username) {
          setUser({ ...currentUser, ...form });
          onUserUpdated?.();
        }
        flash('success', 'Changes saved.');
      }
      closeDetail();
      fetchUsers();
      fetchFunctions();
    } catch (e) {
      flash('error', e.message);
    }
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_USERS}/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Delete failed');
      flash('success', 'User deleted.');
      setConfirmId(null);
      if (selected?.id === id) closeDetail();
      fetchUsers();
      fetchFunctions();
    } catch (e) {
      flash('error', e.message);
    }
  };

  // ── Filter ──
  const filtered = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [u.first_name, u.last_name, u.username, u.email].some(v => v?.toLowerCase().includes(s));
  });

  // ───────────────────────────────────────
  // RENDER: Detail view
  // ───────────────────────────────────────
  if (selected && form) {
    const funcIds = form.llm_config?.bot_function_ids || [];
    const expandables = form.ui_config?.expandables || [];

    const enabledFunctions = funcIds.map(id => botFunctions.find(f => f.id === id)).filter(Boolean);
    const availableFunctions = botFunctions.filter(f => !funcIds.includes(f.id));

    const enabledExpandables = expandables.map(v => EXPANDABLE_OPTIONS.find(o => o.value === v)).filter(Boolean);
    const availableExpandables = EXPANDABLE_OPTIONS.filter(o => !expandables.includes(o.value));

    return (
      <div className="um-page">
        {/* Top bar */}
        <div className="um-topbar">
          <div className="um-topbar-left">
            <button className="um-back-btn" onClick={closeDetail}><FontAwesomeIcon icon={faArrowLeft} /></button>
            <h1 className="um-topbar-title">{isNew ? <>New <span>User</span></> : <>{form.first_name} <span>{form.last_name}</span></>}</h1>
          </div>
        </div>

        {toast && <div className={`um-toast um-toast-${toast.type}`}>{toast.text}</div>}

        <div className="um-detail-card">
          {/* ── Section: User Info ── */}
          <div className="um-section">
            <div className="um-section-head" onClick={() => toggleSection('user')}>
              <FontAwesomeIcon icon={faUser} className="um-section-icon" />
              <h3>User Information</h3>
              <FontAwesomeIcon icon={faChevronDown} className={`um-section-chevron${collapsed.user ? ' collapsed' : ''}`} />
            </div>
            <div className={`um-fields${collapsed.user ? ' um-collapsed' : ''}`}>
              <div className="um-f">
                <label>First Name</label>
                <input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First name" />
              </div>
              <div className="um-f">
                <label>Last Name</label>
                <input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Last name" />
              </div>
              <div className="um-f">
                <label>Username</label>
                <input value={form.username} onChange={e => set('username', e.target.value)} placeholder="Username" />
              </div>
              <div className="um-f">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email address" />
              </div>
              <div className="um-f">
                <label>Password</label>
                <input value={form.hashed_password} onChange={e => set('hashed_password', e.target.value)} placeholder="Password" />
              </div>
              <div className="um-f">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="um-f">
                <label>Language</label>
                <select value={form.language} onChange={e => set('language', e.target.value)}>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="es">Español</option>
                  <option value="it">Italiano</option>
                </select>
              </div>
              {!isNew && (
                <div className="um-f">
                  <label>Last Login</label>
                  <input value={form.last_login || '—'} disabled />
                </div>
              )}
            </div>
          </div>

          {/* ── Section: LLM Config ── */}
          <div className="um-section">
            <div className="um-section-head" onClick={() => toggleSection('llm')}>
              <FontAwesomeIcon icon={faRobot} className="um-section-icon" />
              <h3>LLM Configuration</h3>
              <FontAwesomeIcon icon={faChevronDown} className={`um-section-chevron${collapsed.llm ? ' collapsed' : ''}`} />
            </div>
            <div className={`um-fields${collapsed.llm ? ' um-collapsed' : ''}`}>
              <div className="um-f">
                <label>Model</label>
                <select value={form.llm_config?.llm_model || ''} onChange={e => set('llm_config.llm_model', e.target.value)}>
                  <option value="">None</option>
                  {LLM_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="um-f">
                <label>Temperature</label>
                <div className="um-slider-wrap">
                  <input
                    type="range" className="um-slider"
                    min="0" max="1" step="0.05"
                    value={form.llm_config?.temperature ?? 0.7}
                    onChange={e => set('llm_config.temperature', parseFloat(e.target.value))}
                  />
                  <span className="um-slider-val">{(form.llm_config?.temperature ?? 0.7).toFixed(2)}</span>
                </div>
              </div>
              <div className="um-f wide">
                <label>System Prompt</label>
                <textarea
                  rows={3}
                  value={form.llm_config?.system_prompt || ''}
                  onChange={e => set('llm_config.system_prompt', e.target.value)}
                  placeholder="Custom system prompt for this user..."
                />
              </div>
              <div className="um-f wide">
                <label>Bot Functions ({funcIds.length} selected)</label>
                <div className="um-check-grid">
                  {enabledFunctions.length > 0 && (
                    <>
                      <div className="um-check-section-label">Enabled</div>
                      {enabledFunctions.map((fn, idx) => (
                        <div key={fn.id}
                          className={`um-check-item selected${drag?.type === 'func' && drag.overIdx === idx ? ' drag-over' : ''}`}
                          onClick={() => toggleFuncId(fn.id)}
                          draggable
                          onDragStart={(e) => handleDragStart(e, 'func', idx)}
                          onDragOver={(e) => handleDragOver(e, 'func', idx)}
                          onDrop={(e) => handleDrop(e, 'func', idx)}
                          onDragEnd={() => setDrag(null)}
                        >
                          <div className="um-drag-handle" onClick={e => e.stopPropagation()}><FontAwesomeIcon icon={faGripVertical} /></div>
                          <div className="um-check-box"><FontAwesomeIcon icon={faCheck} /></div>
                          <div className="um-check-label">
                            <div className="um-check-name">{fn.name}</div>
                            <div className="um-check-desc">{fn.description}</div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {availableFunctions.length > 0 && (
                    <>
                      <div className="um-check-section-label">Available</div>
                      {availableFunctions.map(fn => (
                        <div key={fn.id}
                          className="um-check-item"
                          onClick={() => toggleFuncId(fn.id)}
                        >
                          <div className="um-drag-handle disabled"><FontAwesomeIcon icon={faGripVertical} /></div>
                          <div className="um-check-box"><FontAwesomeIcon icon={faCheck} /></div>
                          <div className="um-check-label">
                            <div className="um-check-name">{fn.name}</div>
                            <div className="um-check-desc">{fn.description}</div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Section: UI Config ── */}
          <div className="um-section">
            <div className="um-section-head" onClick={() => toggleSection('ui')}>
              <FontAwesomeIcon icon={faPalette} className="um-section-icon" />
              <h3>UI Configuration</h3>
              <FontAwesomeIcon icon={faChevronDown} className={`um-section-chevron${collapsed.ui ? ' collapsed' : ''}`} />
            </div>
            <div className={`um-fields${collapsed.ui ? ' um-collapsed' : ''}`}>
              {/* <div className="um-f">
                <label>View Layout</label>
                <select value={form.ui_config?.view || ''} onChange={e => set('ui_config.view', e.target.value)}>
                  {VIEW_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div> */}
              {/* <div className="um-f">
                <label>Logo</label>
                <select value={form.ui_config?.logo || 'default'} onChange={e => set('ui_config.logo', e.target.value)}>
                  {LOGO_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div> */}
              {/* <div className="um-f">
                <label>PDF URL</label>
                <input value={form.ui_config?.pdf_url || ''} onChange={e => set('ui_config.pdf_url', e.target.value)} placeholder="/path/to/file.pdf" />
              </div>
              <div className="um-f">
                <label>PDF Download Name</label>
                <input value={form.ui_config?.pdf_download_name || ''} onChange={e => set('ui_config.pdf_download_name', e.target.value)} placeholder="download.pdf" />
              </div> */}
              <div className="um-f">
                <label>Show PDF Download</label>
                <div className="um-toggle-wrap">
                  <label className="um-switch">
                    <input type="checkbox" checked={!!form.ui_config?.show_pdf_download} onChange={e => set('ui_config.show_pdf_download', e.target.checked)} />
                    <span className="um-switch-track"></span>
                  </label>
                  <span className="um-switch-label">{form.ui_config?.show_pdf_download ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <div className="um-f">
                <label>Show Breaches Button</label>
                <div className="um-toggle-wrap">
                  <label className="um-switch">
                    <input type="checkbox" checked={!!form.ui_config?.show_breaches_button} onChange={e => set('ui_config.show_breaches_button', e.target.checked)} />
                    <span className="um-switch-track"></span>
                  </label>
                  <span className="um-switch-label">{form.ui_config?.show_breaches_button ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <div className="um-f wide">
                <label>Expandable Components ({expandables.length} selected)</label>
                <div className="um-check-grid">
                  {enabledExpandables.length > 0 && (
                    <>
                      <div className="um-check-section-label">Enabled</div>
                      {enabledExpandables.map((opt, idx) => (
                        <div key={opt.value}
                          className={`um-check-item selected${drag?.type === 'exp' && drag.overIdx === idx ? ' drag-over' : ''}`}
                          onClick={() => toggleExpandable(opt.value)}
                          draggable
                          onDragStart={(e) => handleDragStart(e, 'exp', idx)}
                          onDragOver={(e) => handleDragOver(e, 'exp', idx)}
                          onDrop={(e) => handleDrop(e, 'exp', idx)}
                          onDragEnd={() => setDrag(null)}
                        >
                          <div className="um-drag-handle" onClick={e => e.stopPropagation()}><FontAwesomeIcon icon={faGripVertical} /></div>
                          <div className="um-check-box"><FontAwesomeIcon icon={faCheck} /></div>
                          <div className="um-check-label">
                            <div className="um-check-name">{opt.label}</div>
                            <div className="um-check-desc">{opt.desc}</div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {availableExpandables.length > 0 && (
                    <>
                      <div className="um-check-section-label">Available</div>
                      {availableExpandables.map(opt => (
                        <div key={opt.value}
                          className="um-check-item"
                          onClick={() => toggleExpandable(opt.value)}
                        >
                          <div className="um-drag-handle disabled"><FontAwesomeIcon icon={faGripVertical} /></div>
                          <div className="um-check-box"><FontAwesomeIcon icon={faCheck} /></div>
                          <div className="um-check-label">
                            <div className="um-check-name">{opt.label}</div>
                            <div className="um-check-desc">{opt.desc}</div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="um-actions">
            {!isNew && (
              <button className="um-btn um-btn-delete" onClick={() => setConfirmId(selected.id)}>
                <FontAwesomeIcon icon={faTrash} /> Delete User
              </button>
            )}
            <button className="um-btn um-btn-cancel" onClick={closeDetail}>Cancel</button>
            <button className="um-btn um-btn-save" onClick={handleSave}>
              {isNew ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </div>

        {confirmId && (
          <div className="um-overlay" onClick={() => setConfirmId(null)}>
            <div className="um-dialog" onClick={e => e.stopPropagation()}>
              <h3>Delete User?</h3>
              <p>This action cannot be undone.</p>
              <div className="um-dialog-btns">
                <button className="um-btn um-btn-cancel" onClick={() => setConfirmId(null)}>Cancel</button>
                <button className="um-btn um-btn-delete" onClick={() => handleDelete(confirmId)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ───────────────────────────────────────
  // RENDER: User list
  // ───────────────────────────────────────
  return (
    <div className="um-page">
      <div className="um-topbar">
        <div className="um-topbar-left">
          <button className="um-back-btn" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /></button>
          <h1 className="um-topbar-title">User <span>Management</span></h1>
        </div>
        <button className="um-create-btn" onClick={openNew}>
          <FontAwesomeIcon icon={faPlus} /> <span>New User</span>
        </button>
      </div>

      {toast && <div className={`um-toast um-toast-${toast.type}`}>{toast.text}</div>}

      <div className="um-search">
        <FontAwesomeIcon icon={faSearch} className="um-search-icon" />
        <input placeholder="Search by name, username or email..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="um-loading"><div className="um-spinner"></div></div>
      ) : filtered.length === 0 ? (
        <div className="um-empty">No users found.</div>
      ) : (
        <div className="um-grid">
          {filtered.map(u => (
            <div key={u.id} className="um-card" onClick={() => openEdit(u)}>
              <div className="um-card-left">
                <div className="um-avatar">{initials(u.first_name, u.last_name)}</div>
                <div className="um-card-info">
                  <div className="um-card-name">{u.first_name} {u.last_name}</div>
                  <div className="um-card-sub">@{u.username} · {u.email || 'No email'}</div>
                </div>
              </div>
              <div className="um-card-right">
                <span className={`um-badge ${u.status === 'active' ? 'um-badge-active' : 'um-badge-inactive'}`}>{u.status}</span>
                <button className="um-card-btn" onClick={e => { e.stopPropagation(); openEdit(u); }} title="Edit">
                  <FontAwesomeIcon icon={faPen} />
                </button>
                <button className="um-card-btn danger" onClick={e => { e.stopPropagation(); setConfirmId(u.id); }} title="Delete">
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmId && (
        <div className="um-overlay" onClick={() => setConfirmId(null)}>
          <div className="um-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete User?</h3>
            <p>This action cannot be undone.</p>
            <div className="um-dialog-btns">
              <button className="um-btn um-btn-cancel" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="um-btn um-btn-delete" onClick={() => handleDelete(confirmId)}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
